import { sand, violetA } from "@radix-ui/colors";
import { Flex, Spacer, Text, ThemeProvider } from "@repo/ui";
import Logo from "./assets/logos/Logo.svg?react";
import CarnegieMellonUniversityLogo from "./assets/logos/cmu.svg?react";
import PalantirLogo from "./assets/logos/palantir.svg?react";
import YCombinatorLogo from "./assets/logos/y-combinator.svg?react";
import MichaelKSignature from "./assets/media/michael-k-signature.svg?react";
import TanishqKSignature from "./assets/media/tanishq-k-signature.svg?react";
import { PolarCoordinateGrid } from "./components/Diagrams";
import { HeaderSection } from "./components/Sections";
import { HeroSubtitle, HeroTitle } from "./components/Typography";
// import { RequestDemoButton } from "./components/UI";

const postmortemMarkdown = `# Moving on from Saffron

After working for over a year on Saffron Health, we're (mostly) pivoting out of the healthcare space. We wanted to share our journey in the hope that it helps other current or aspiring health-tech founders.

## Why we started in healthcare

When we first started Saffron Health in early 2025, we set out to help patients directly. Both of us had poor experiences with the healthcare system. Michael had struggled for years from chronic insomnia, and Tanishq's dad had a doctor mistreat his knee, forcing a knee replacement surgery.

We started building a consumer AI agent for patients with chronic illnesses that could help them find doctors, schedule appointments, and manage doctor's notes. After conversations with people deeper in healthcare, we got cold feet on the consumer approach and pivoted to selling our product as B2B instead. Specifically, we became interested in capitalizing on [APCM](https://www.cms.gov/medicare/payment/fee-schedules/physician-fee-schedule/advanced-primary-care-management-services), a new Medicare initiative that could let providers get paid for providing chronic illness support outside of the office. That meant our product could be a win-win: providers could make money and consumers got access to it for free.

Both of us regret that we didn't launch the consumer app idea. We were told that it's hard for consumer apps to monetize or gain traction. But now there are apps like [Juno](https://junocompanion.com/) proving that wrong.

We got accepted into the YC Spring 2025 batch with the APCM product idea, but after lots of conversations and disinterested providers, we decided to pivot again; this time into send-side referral automation. The revenue they could gain from APCM was minimal given their patient panel.

We found this idea while working closely with Hamilton Health Box, and realized the referral process was extremely time-consuming and manual. In addition, patients were also getting sent to places that didn't take their insurance or didn't have availability for many months.

## Inside the referral workflow

Some background on how send-side referrals work: when a PCP makes a referral to a specialty for a patient, an MA has to find providers in the area, often relying on Google, while taking into account patient preferences like location. They could blindly send the referral to a provider who might not even take the patient, or they could call different providers verifying their insurance acceptance and availability. For HMO patients, they also need to collect the name and NPI of the provider to use for the referral prior authorization.

Then they submit a referral authorization form on the payor portal for HMO plans, fax the referral along with relevant documents to the clinic, and notify the patient. The specialty clinic is supposed to call the patient to set up an appointment, and after the appointment is attended, the primary care clinic needs to call the specialist's office to collect the consult notes.

Almost every step of this process is unreliable: clinics will say they haven't received the fax, patients will be unresponsive, payor portals go down for hours at a time. Keeping track of every referral takes up an MA's full time.

After meeting Hamilton Health Box in June of 2025, we became their manual referral coordinators: calling patients, having our own portal logins, and more for about five months. We had almost daily calls with one of their MAs, Kasey, who was immensely helpful in teaching us. Simultaneously, we slowly started building out an AI platform to help us automate pieces of that work. I fundamentally believed doing the work ourselves first helped us build a much better product.

## The product worked. The market didn't.

Around October, we started handing off our platform to their real referral coordinators, and they loved it. They were using it for real referrals, assisting patients get to their appointments faster, and it was saving them hours every day. The agent handled making phone calls to specialists to retrieve information, filling out auths on payor portals, and could close referrals end-to-end. Staff even gave the agent a name: Chad.

The pilot converted and we started making some money. After that, we [launched](https://www.linkedin.com/feed/update/urn:li:ugcPost:7392250287807377410/) our company publicly in November and immediately started having sales conversations.

Fast-forward to February of this year. We were getting conversations, but not at the volume we needed, and unable to convert any of them. We repeatedly got the feedback that our product and tech was awesome and would save them a ton of time, but referrals just weren't a priority right now. Surprisingly, this was the case for both regular fee-for-service as well as value-based care.

On the flip side, we talked to a few other health-tech founders and they seemed increasingly interested in the internal tooling we built to manage our browser integrations. Finally, one day, Michael walked out of another lost sale, and we made the decision to start exploring the pivot seriously.

## Where we're going next

We're pivoting to [Libretto](https://libretto.sh), the best way for your agents to turn website workflows into fast, reusable scripts. Stay tuned for our launch.

When building Saffron, our biggest operational burden by far was third-party integrations. Since we didn't want providers to have to learn a new app UI to create referrals, we had to use browser automation to scrape referrals from their existing EHRs and submit referral authorizations on various payor portals.

We started by using browser agents like Stagehand and Browser Use for this, but quickly found them to be slow, expensive, and unreliable. We're dealing with patient data and browser agents have no guardrails.

So we started prototyping direct browser automation scripts instead. But these scripts took weeks to build and maintaining those automations from website drift easily took up a whole engineer's capacity each day.

We ended up building a ton of internal tooling to help us manage the work. This tooling is what we eventually open-sourced as Libretto.

## Learnings from building in healthcare

Some learnings we had, in no particular order:

### Michael's learnings

- Selling into healthcare the conventional way, through providers, payers, and employers, taught us that incentives shape almost everything: what gets prioritized, what gets budget, and what actually gets adopted. We originally wanted to build something that improved patient experience in a way that did not line up cleanly with reimbursement, and what we learned the hard way is that it is really difficult to build a durable business with hand-wavy incentives.
- We waited too long to launch because we convinced ourselves the referral product needed to be fully autonomous before we put it out in the world. In hindsight, I would have launched as soon as possible to get more conversations and learn faster that there wasn't enough demand for what we were building. We were betting that people would want to buy fully autonomous solutions, which naturally took a while to build. But we still could have launched much faster. We also never launched the consumer app we built, even though we had a super interesting product.
- We learned to be more honest about where we actually had an edge and choose a wedge that matched it. We are strongest when we can combine good product instincts with deep technical execution, and that helped a lot when we were building the platform, but it helped less when we were trying to navigate reimbursement programs and healthcare incentives without much prior context in the space.
- As first-time founders, a huge chunk of what we learned had nothing to do with the product we were building, but just the basics of learning how to be a founder: validating ideas, doing outbound, building a stable working rhythm with a cofounder, and doing uncomfortable things to learn and try to grow the business. People sometimes say that working at other companies can prepare you for this, but that hasn't been my experience. Learning this has been super important, and has helped us hone in more on who we are as founders.

### Tanishq's learnings

- The health-tech community is incredibly kind and helpful. They really understand how hard it is to run a health-tech company and how closed healthcare is generally, and communities try very hard to make it accessible for new people. Finding people in communities and hitting them up for conversations was the fastest way for us to learn.
- Network is extremely important in healthcare. Neither of us had a very deep healthcare background and our outbound was all generally cold. Although, we were fortunate enough to have found Hamilton Health Box.
- Healthcare is really complicated. There are tons of players and revenue models can get very convoluted. We understood a very, very tiny portion of all there is to learn. This goes back to my previous point that having a deep healthcare background is a huge advantage. You come in with all of that knowledge built in.
- AI has a ton of potential in healthcare. There is so much unbelievable opportunity in just embedding yourself in a clinical setting and seeing all the inefficiencies that can be automated with AI. But I'm personally more excited about companies trying to boil the ocean and rebuild systems from scratch.
- AI healthcare company websites are annoying. All of them just say they do "workflow automation" and forego any further detail. I wish they just explained what they did.

## Acknowledgements

Thank you to **Hamilton Healthbox** (**Rosa, Danielle, Isaac, Ashish, Austin, Shane, Harrison**, and everyone else) for trusting us early, letting us work so closely with your team, and giving us the chance to learn by doing. We're especially grateful to **Kasey H.** for patiently teaching us how referrals actually work in the real world.

We're also thankful to **Paulius and the XPC community** for accepting us as fellows, opening doors, and always being generous with guidance and conversations. Thank you to **James L.**, **Shobha D.**, **Sathya H.**, and many others who helped us understand healthcare, made introductions, or were simply kind when we were still figuring things out.

More broadly, thank you to everyone in the healthcare community who took the time to teach us. One of the best parts of working in health-tech was seeing how willing people were to help new founders navigate an incredibly hard space.`;

type MarkdownBlock =
	| { type: "heading"; level: number; text: string }
	| { type: "paragraph"; text: string }
	| { type: "list"; items: string[] };

const parseMarkdownBlocks = (markdown: string): MarkdownBlock[] => {
	const blocks: MarkdownBlock[] = [];
	const lines = markdown.trim().split("\n");

	for (let index = 0; index < lines.length; index += 1) {
		const line = lines[index]?.trim();

		if (!line) {
			continue;
		}

		const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
		if (headingMatch?.[1] && headingMatch[2]) {
			blocks.push({
				type: "heading",
				level: headingMatch[1].length,
				text: headingMatch[2],
			});
			continue;
		}

		if (line.startsWith("- ")) {
			const items: string[] = [];
			while (index < lines.length) {
				const itemLine = lines[index]?.trim();
				if (!itemLine?.startsWith("- ")) {
					index -= 1;
					break;
				}
				items.push(itemLine.slice(2));
				index += 1;
			}
			blocks.push({ type: "list", items });
			continue;
		}

		const paragraphLines = [line];
		while (index + 1 < lines.length) {
			const nextLine = lines[index + 1]?.trim();
			if (
				!nextLine ||
				nextLine.startsWith("#") ||
				nextLine.startsWith("- ")
			) {
				break;
			}
			paragraphLines.push(nextLine);
			index += 1;
		}
		blocks.push({ type: "paragraph", text: paragraphLines.join(" ") });
	}

	return blocks;
};

const renderInlineMarkdown = (text: string): React.ReactNode[] => {
	const nodes: React.ReactNode[] = [];
	const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
	let cursor = 0;
	let match: RegExpExecArray | null;

	while ((match = pattern.exec(text))) {
		if (match.index > cursor) {
			nodes.push(text.slice(cursor, match.index));
		}

		const token = match[0];
		const key = `${match.index}-${token}`;
		const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

		if (linkMatch) {
			nodes.push(
				<a key={key} href={linkMatch[2]} target="_blank" rel="noreferrer">
					{linkMatch[1]}
				</a>,
			);
		} else {
			nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
		}

		cursor = match.index + token.length;
	}

	if (cursor < text.length) {
		nodes.push(text.slice(cursor));
	}

	return nodes;
};

const SignatureBlock = ({
	name,
	children,
}: {
	name: string;
	children: React.ReactNode;
}) => {
	return (
		<div className="postmortem-signature">
			<div className="postmortem-signature-mark">{children}</div>
			<div className="postmortem-signature-name">{name}</div>
		</div>
	);
};

const PostmortemArticle = () => {
	const blocks = parseMarkdownBlocks(postmortemMarkdown);

	return (
		<article className="postmortem-markdown">
			{blocks.map((block, index) => {
				if (block.type === "heading") {
					const HeadingTag = `h${block.level}` as "h1" | "h2" | "h3";
					return (
						<HeadingTag key={index}>{renderInlineMarkdown(block.text)}</HeadingTag>
					);
				}

				if (block.type === "list") {
					return (
						<ul key={index}>
							{block.items.map((item, itemIndex) => (
								<li key={itemIndex}>{renderInlineMarkdown(item)}</li>
							))}
						</ul>
					);
				}

				return <p key={index}>{renderInlineMarkdown(block.text)}</p>;
			})}
			<div className="postmortem-signatures" aria-label="Author signatures">
				<SignatureBlock name="Michael Kronovet">
					<MichaelKSignature />
				</SignatureBlock>
				<SignatureBlock name="Tanishq Kancharla">
					<TanishqKSignature />
				</SignatureBlock>
			</div>
		</article>
	);
};

export const OpenGraphImage: React.FC = () => {
	return (
		<Flex
			column
			align="center"
			paddingVertical="xxl"
			style={{
				position: "relative",
				overflow: "hidden",
				width: 1200,
				height: 630,
				backgroundColor: sand.sand1,
			}}
		>
			<div
				style={{
					position: "absolute",
					inset: 0,
					zIndex: 0,
					overflow: "hidden",
				}}
			>
				<div
					className="hero-gradient"
					style={{ position: "absolute", inset: 0, height: "130%" }}
				/>
				<div
					style={{
						position: "absolute",
						inset: 0,
						transform: `translateY(-50%)`,
					}}
				>
					<PolarCoordinateGrid
						initialR={80}
						circleCount={3}
						rStep={120}
						color={violetA.violetA4}
					/>
				</div>
			</div>
			<Flex column align="center" paddingHorizontal="lg" style={{ zIndex: 1 }}>
				<Spacer style={{ height: 64 }} />
				<Flex
					paddingHorizontal="md"
					paddingVertical="xs"
					align="center"
					gap="xxs"
					background="sandA1"
					borderRadius="full"
					shadow="elevated"
					style={{
						backdropFilter: "blur(10px)",
						scale: 9,
					}}
				>
					<Logo width={24} height={24} />
					<Text size="lg" weight="semibold" color="sand12">
						Saffron Health
					</Text>
				</Flex>
				<Spacer flex height="xxxl" />
				<HeroTitle>
					Automate specialist referrals from start to finish
				</HeroTitle>
				<Spacer height="xxl" />
				<Flex column align="center" paddingHorizontal="md">
					<HeroSubtitle>
						Let AI agents submit authorizations, book appointments, fax
						documents, notify patients and retrieve consult notes — all on
						autopilot
					</HeroSubtitle>
				</Flex>
				<Spacer style={{ height: 48 }} />
				<Flex align="center" justify="center" gap="xxxxl" style={{ scale: 2 }}>
					<PalantirLogo
						opacity={0.7}
						style={{ maxWidth: 120, maxHeight: 50 }}
					/>
					<CarnegieMellonUniversityLogo
						opacity={0.7}
						style={{ maxWidth: 120, maxHeight: 50 }}
					/>
					<YCombinatorLogo
						opacity={0.7}
						style={{ maxWidth: 120, maxHeight: 50 }}
					/>
				</Flex>
			</Flex>
		</Flex>
	);
};

export function Website() {
	// return (
	// 	<ThemeProvider forceMode="light">
	// 		<OpenGraphImage />
	// 	</ThemeProvider>
	// );
	return (
		<ThemeProvider forceMode="light">
			<Flex
				column
				background="sand1"
				style={{ color: sand.sand12, position: "relative", overflowX: "clip" }}
			>
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: "120vh",
						zIndex: 0,
					}}
				>
					<div
						className="hero-gradient"
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							height: "100%",
						}}
					/>
					<div
						style={{
							position: "absolute",
							inset: 0,
							transform: `translateY(-50%)`,
						}}
					>
						<PolarCoordinateGrid
							initialR={80}
							circleCount={3}
							rStep={120}
							color={violetA.violetA4}
						/>
					</div>
				</div>
				<Flex
					align="center"
					justify="center"
					paddingHorizontal="md"
					style={{
						position: "sticky",
						top: 16,
						zIndex: 100,
						paddingTop: 16,
						paddingBottom: 16,
					}}
				>
					<Flex
						paddingHorizontal="lg"
						paddingVertical="sm"
						align="center"
						justify="space-between"
						background="sandA1"
						borderRadius="full"
						shadow="elevated"
						style={{
							maxWidth: 640,
							width: "100%",
							backdropFilter: "blur(10px)",
						}}
					>
						<Flex gap="xxs" align="center">
							<Logo width={24} height={24} />
							<Text size={"lg"} weight={"semibold"} color="sand12">
								Saffron Health
							</Text>
						</Flex>
						{/* <RequestDemoButton size="small" location="header" /> */}
					</Flex>
				</Flex>
				<Flex
					column
					align="stretch"
					style={{
						position: "relative",
						zIndex: 2,
					}}
				>
					<HeaderSection />
				</Flex>
				<Spacer style={{ height: 72 }} mobile={{ style: { height: 40 } }} />
				<Flex
					align="center"
					justify="center"
					paddingHorizontal="md"
					style={{ position: "relative", zIndex: 2 }}
				>
					<PostmortemArticle />
				</Flex>
				<Spacer style={{ height: 72 }} mobile={{ style: { height: 40 } }} />
			</Flex>
		</ThemeProvider>
	);
}
